import React, { useEffect, useState } from 'react';
import { History, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const LABELS = {
  create: 'Created', update: 'Edited', issue: 'Issued', cancel: 'Canceled', reopen: 'Reopened as Draft', delete: 'Deleted',
  fulfill: 'Fulfilled', ship: 'Shipped', deliver: 'Delivered', complete: 'Completed', start: 'Started', close: 'Closed',
  email_sent: 'Emailed', spawn_work_orders: 'Work orders spawned', spawn_work_order: 'Work order spawned',
  machine_status: 'Machine status changed', customer_promoted: 'Customer saved to list', receive: 'Received',
};
const TONE = { cancel: 'bg-red-500', delete: 'bg-red-500', reopen: 'bg-orange-500', create: 'bg-slate-900', issue: 'bg-blue-600' };
const label = (a) => LABELS[a] || (a || '').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

const detailText = (h) => {
  const d = h.details || {};
  const bits = [];
  if (h.from_status) bits.push(`was ${h.from_status}`);
  if (d.status && h.action === 'machine_status') bits.push(`→ ${d.status}`);
  if (d.to) bits.push(`to ${Array.isArray(d.to) ? d.to.join(', ') : d.to}`);
  if (h.fields?.length) bits.push(`changed ${h.fields.slice(0, 6).join(', ')}${h.fields.length > 6 ? '…' : ''}`);
  return bits.join(' · ');
};

// Collapsible audit trail for SO / PO / PS / RO detail pages.
export const DocHistoryPanel = ({ entityType, docId, refreshKey }) => {
  const { api } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!docId) return;
    api().get(`/doc-history/${entityType}/${docId}`).then(r => setRows(r.data || [])).catch(() => setRows([]));
  }, [api, entityType, docId, refreshKey]);
  return (
    <Card className="industrial-card" data-testid="doc-history-panel">
      <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setOpen(o => !o)} data-testid="doc-history-toggle">
        <CardTitle className="text-base flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}<History className="w-4 h-4" /> History
          <span className="text-xs font-normal text-slate-500">({rows ? rows.length : '…'} events)</span>
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent className="pt-0 pb-4 px-6">
          {!rows?.length ? <p className="text-sm text-slate-400">No history recorded yet.</p> : (
            <ol className="relative border-l border-slate-200 ml-1.5 space-y-3" data-testid="doc-history-list">
              {rows.map((h, i) => (
                <li key={i} className="ml-4" data-testid={`doc-history-entry-${i}`}>
                  <span className={`absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full ${TONE[h.action] || 'bg-slate-400'}`} />
                  <div className="text-sm"><b>{label(h.action)}</b> <span className="text-slate-500">by {h.by}</span> <span className="text-xs text-slate-400">· {h.at ? new Date(h.at).toLocaleString() : ''}</span></div>
                  {detailText(h) && <div className="text-xs text-slate-500">{detailText(h)}</div>}
                  {h.reason && <div className="text-xs mt-0.5 italic text-slate-700" data-testid={`doc-history-reason-${i}`}>"{h.reason}"</div>}
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      )}
    </Card>
  );
};
